/**
 * Date utility functions for handling timezone-sensitive operations
 * Based on the working logic from ProjectList.js
 */

/**
 * Creates a Date object for calendar events that preserves the intended calendar date
 * regardless of timezone differences. This prevents the common issue where due dates
 * shift by one day when UTC timestamps are converted to local timezone.
 * 
 * This function extracts the UTC date components (year, month, day) from the database
 * timestamp and creates a new Date object using those components in local timezone.
 * 
 * @param {string} dateString - ISO date string from database (e.g., "2024-09-28T00:00:00.000Z")
 * @returns {Date|null} - Date object in local timezone representing the same calendar date
 */
export const createCalendarDate = (dateString) => {
  if (!dateString) return null;
  
  try {
    // Create date in UTC to preserve the day value stored in the database
    const date = new Date(dateString);
    
    // Use the UTC date components to create a date at noon local time
    // This prevents timezone issues from shifting the displayed date
    const utcDay = date.getUTCDate();
    const utcMonth = date.getUTCMonth();
    const utcYear = date.getUTCFullYear();
    
    // Create a new date using the UTC components but in local time
    const localDate = new Date();
    localDate.setFullYear(utcYear, utcMonth, utcDay);
    localDate.setHours(12, 0, 0, 0); // Set to noon to avoid DST issues
    
    return localDate;
  } catch (error) {
    console.warn('Error creating calendar date:', dateString, error);
    return null;
  }
};

/**
 * Formats a date for display (reusable version of ProjectList logic)
 * @param {string} dateString - ISO date string from database
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'Not set';
  
  try {
    const localDate = createCalendarDate(dateString);
    if (!localDate) return 'Invalid date';
    
    return localDate.toLocaleDateString();
  } catch (e) {
    console.warn('Invalid date format:', dateString);
    return 'Invalid date';
  }
};

/**
 * Calculates due date status (overdue, warning, normal)
 * @param {string} dateString - ISO date string from database
 * @returns {string} - Status: 'overdue', 'warning', 'normal', or 'none'
 */
export const getDueDateStatus = (dateString) => {
  if (!dateString) return 'none';
  
  try {
    const dueDate = createCalendarDate(dateString);
    if (!dueDate) return 'none';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Calculate difference in days
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'overdue'; // Past due date
    } else if (diffDays <= 7) {
      return 'warning'; // Due within a week
    } else {
      return 'normal'; // Due date is more than 7 days away
    }
  } catch (e) {
    console.warn('Error calculating due date status:', e);
    return 'none';
  }
};

/**
 * Gets hex color based on due date status for calendar components
 * @param {string} dateString - ISO date string from database
 * @returns {string} - Hex color code
 */
export const getDueDateStatusColor = (dateString) => {
  if (!dateString) return '#6B7280'; // gray-500
  
  try {
    const dueDate = createCalendarDate(dateString);
    if (!dueDate) return '#6B7280';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return '#EF4444'; // red-500 - Overdue
    } else if (diffDays <= 7) {
      return '#F97316'; // orange-500 - Due soon
    } else {
      return '#22C55E'; // green-500 - Due later
    }
  } catch (e) {
    console.warn('Error calculating due date status color:', e);
    return '#6B7280'; // gray-500
  }
};

/**
 * Formats a date for input fields (YYYY-MM-DD format)
 * @param {string} dateString - ISO date string from database
 * @returns {string} - Date in YYYY-MM-DD format for input fields
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  } catch (e) {
    console.warn('Invalid date format for input:', dateString);
    return '';
  }
};
